import { useCursor } from '@react-three/drei'
import { type ThreeElements } from '@react-three/fiber'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Matrix4, Mesh, Ray, Sphere, Vector3, type Intersection, type Raycaster } from 'three'

type SphereHitboxProps = Omit<ThreeElements['mesh'], 'children'> & {
  debug?: boolean
  radius?: number
}

export function SphereHitbox({
  debug,
  radius = 1,
  onPointerOver,
  onPointerOut,
  onPointerEnter,
  onPointerLeave,
  ...props
}: SphereHitboxProps) {
  const ref = useRef<Mesh>(null)

  const sphere = useMemo(() => new Sphere(new Vector3(), radius), [radius])
  const inverseMatrix = useMemo(() => new Matrix4(), [])
  const localRay = useMemo(() => new Ray(), [])
  const hitPoint = useMemo(() => new Vector3(), [])

  const [hovered, setHovered] = useState(false)
  useCursor(hovered)

  useEffect(() => {
    return () => setHovered(false)
  }, [])

  const raycast = useCallback(
    (raycaster: Raycaster, intersects: Intersection[]) => {
      const mesh = ref.current
      if (!mesh) return

      mesh.updateWorldMatrix(true, false)
      inverseMatrix.copy(mesh.matrixWorld).invert()
      localRay.copy(raycaster.ray).applyMatrix4(inverseMatrix)

      if (!localRay.intersectSphere(sphere, hitPoint)) return

      hitPoint.applyMatrix4(mesh.matrixWorld)
      const distance = raycaster.ray.origin.distanceTo(hitPoint)

      if (distance < raycaster.near || distance > raycaster.far) return

      intersects.push({
        distance,
        point: hitPoint.clone(),
        face: null,
        object: mesh,
      } as Intersection)
    },
    [sphere, inverseMatrix, localRay, hitPoint],
  )

  return (
    <mesh
      ref={ref}
      raycast={raycast}
      onPointerOver={e => {
        e.stopPropagation()
        setHovered(true)
        // @ts-expect-error r3f types mismatch
        onPointerOver?.(e)
      }}
      onPointerOut={e => {
        e.stopPropagation()
        setHovered(false)
        // @ts-expect-error r3f types mismatch
        onPointerOut?.(e)
      }}
      onPointerEnter={e => {
        e.stopPropagation()
        setHovered(true)
        // @ts-expect-error r3f types mismatch
        onPointerEnter?.(e)
      }}
      onPointerLeave={e => {
        e.stopPropagation()
        setHovered(false)
        // @ts-expect-error r3f types mismatch
        onPointerLeave?.(e)
      }}
      {...props}
    >
      {debug && (
        <mesh raycast={() => null}>
          <sphereGeometry args={[radius, 8, 8]} />
          <meshBasicMaterial color="lime" wireframe opacity={0.4} transparent />
        </mesh>
      )}
    </mesh>
  )
}
